import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { SystemRole } from '@peles/shared';
import type { PublicQuestionnaire } from '@peles/shared';
import { getClientEventQuestionnaires } from '../api/portal';
import { QuestionnaireFillView } from '../components/questionnaires';
import { useAuth } from '../context/AuthContext';
import { isPublicSite } from '../utils/is-public-site';

/**
 * Public no-login questionnaire fill page (/questionnaire/:token). Answers
 * autosave per question; linked questions arrive prefilled from event data.
 *
 * Logged-in clients assigned to the questionnaire's event are redirected to
 * the questionnaire inside their event in the client portal; anonymous
 * visitors get a login button that returns them here after signing in.
 */
export function PublicQuestionnairePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [loaded, setLoaded] = useState<{ id: string; eventId: string | null } | null>(null);
  // The /login and /client routes only exist on the app hostnames.
  const [publicSite] = useState(isPublicSite);

  const handleLoaded = useCallback((questionnaire: PublicQuestionnaire) => {
    setLoaded({ id: questionnaire.id, eventId: questionnaire.event?.id ?? null });
  }, []);

  // Logged-in client on the app host: if they're assigned to this
  // questionnaire's event, take them to the questionnaire inside their event.
  useEffect(() => {
    if (publicSite || authLoading || !isAuthenticated) return;
    if (user?.systemRole !== SystemRole.CLIENT) return;
    if (!loaded?.eventId) return;
    const { id, eventId } = loaded;
    let cancelled = false;
    getClientEventQuestionnaires(eventId)
      .then(() => {
        if (cancelled) return;
        navigate(`/client/events/${eventId}?tab=questionnaires&questionnaire=${id}`, {
          replace: true,
        });
      })
      .catch(() => {
        // Not assigned to this event (or portal unavailable) — stay on the
        // public tokenized page, which works for anyone with the link.
      });
    return () => {
      cancelled = true;
    };
  }, [publicSite, authLoading, isAuthenticated, user, loaded, navigate]);

  const showLogin = !publicSite && !authLoading && !isAuthenticated;
  const loginButton = showLogin ? (
    <Link
      to={`/login?next=${encodeURIComponent(`/questionnaire/${token ?? ''}`)}`}
      className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      data-testid="questionnaire-login-link"
    >
      <LogIn size={14} />
      Log In
    </Link>
  ) : null;

  return (
    <QuestionnaireFillView
      token={token ?? ''}
      variant="public"
      headerActions={loginButton}
      onLoaded={handleLoaded}
    />
  );
}
